import type { ExerciseTemplate } from '@yuan-math/exercises';
import { roundTo } from '@yuan-math/math';
import { choiceExercise, numericExercise, options, template, trap } from './authoring.ts';

/** The value of π these exercises use, stated in every prompt that needs it. */
const PI = 3.14;

/** Angles, circles, scale drawings and surface area — the last of the measurement work. */
export const GEOMETRY_UPPER_TEMPLATES: ExerciseTemplate[] = [
  template({
    id: 'angle-complement-supplement',
    conceptId: 'angles',
    description: 'Find the angle that completes a right angle or a straight line.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const complementary = rng.bool();
      const total = complementary ? 90 : 180;
      const given = rng.int(10, 80);
      const answer = total - given;

      return numericExercise({
        prompt: `Two angles are ${complementary ? 'complementary' : 'supplementary'}. One of them is ${given}°. How big is the other?`,
        unit: 'degrees',
        value: answer,
        summary: `${complementary ? 'Complementary' : 'Supplementary'} angles add to ${total}°, so the other is ${total} − ${given} = ${answer}°.`,
        hint: complementary
          ? 'Complementary angles fit together into a right angle.'
          : 'Supplementary angles fit together along a straight line.',
        steps: [
          { label: `The two angles add to ${total}°.`, latex: `${given} + x = ${total}` },
          { label: 'Subtract.', latex: `x = ${answer}` },
        ],
        traps: [trap((complementary ? 180 : 90) - given, 'complementary-supplementary-swap')],
      });
    },
  }),

  template({
    id: 'angle-triangle-sum',
    conceptId: 'angles',
    description: 'Find the third angle of a triangle.',
    difficulties: [2, 3],
    build: ({ rng, difficulty }) => {
      const first = rng.int(20, difficulty === 2 ? 70 : 100);
      const second = rng.int(20, 150 - first);
      const answer = 180 - first - second;

      return numericExercise({
        prompt: `Two angles of a triangle are ${first}° and ${second}°. What is the third?`,
        unit: 'degrees',
        value: answer,
        summary: `The angles of any triangle add to 180°, so the third is 180 − ${first} − ${second} = ${answer}°.`,
        hint: 'Tear the three corners off any triangle and lay them side by side — they make a straight line.',
        steps: [
          { label: 'The angles of a triangle add to 180°.', latex: `${first} + ${second} + x = 180` },
          { label: 'Subtract what you know.', latex: `x = ${answer}` },
        ],
        traps: [trap(360 - first - second, 'angle-sum-confused')],
      });
    },
  }),

  template({
    id: 'angle-on-a-line',
    conceptId: 'angles',
    description: 'Find the missing angle among angles meeting on a straight line.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const first = rng.int(15, 45);
      const second = rng.int(15, 80 - first);
      const answer = 180 - first - second;

      return numericExercise({
        prompt: `Three angles meet on a straight line. Two of them are ${first}° and ${second}°. How big is the third?`,
        unit: 'degrees',
        value: answer,
        summary: `Angles on a straight line add to 180°, so the third is 180 − ${first} − ${second} = ${answer}°.`,
        hint: 'A straight line is half a full turn.',
        steps: [
          { label: 'Angles on a straight line add to 180°.', latex: `${first} + ${second} + x = 180` },
          { label: 'Subtract.', latex: `x = ${answer}` },
        ],
        // Half a turn confused with a quarter turn.
        traps: [trap(90 - first - second, 'angle-sum-confused')],
      });
    },
  }),

  template({
    id: 'circle-circumference',
    conceptId: 'circles',
    description: 'Find the circumference from the radius or the diameter.',
    difficulties: [2, 3],
    build: ({ rng, difficulty }) => {
      const radius = rng.int(2, difficulty === 2 ? 12 : 25);
      const givenDiameter = rng.bool();
      const diameter = radius * 2;
      const answer = roundTo(PI * diameter, 2);

      return numericExercise({
        prompt: givenDiameter
          ? `A circle has a diameter of ${diameter} cm. What is its circumference? Use π = ${PI}.`
          : `A circle has a radius of ${radius} cm. What is its circumference? Use π = ${PI}.`,
        unit: 'cm',
        ...(givenDiameter ? {} : { figure: { kind: 'shape' as const, shape: 'circle' as const, labels: { radius: `${radius} cm` } } }),
        value: answer,
        summary: `Circumference is π × diameter. The diameter is ${diameter} cm, so C = ${PI} × ${diameter} = ${answer} cm.`,
        hint: 'The circumference is π times the diameter — the whole way across, not half of it.',
        steps: [
          { label: 'Find the diameter.', latex: givenDiameter ? `d = ${diameter}` : `d = 2 \\times ${radius} = ${diameter}` },
          { label: 'Multiply by π.', latex: `C = ${PI} \\times ${diameter} = ${answer}` },
        ],
        traps: [
          givenDiameter
            ? trap(roundTo(PI * diameter * 2, 2), 'diameter-for-radius')
            : trap(roundTo(PI * radius, 2), 'diameter-for-radius'),
        ],
      });
    },
  }),

  template({
    id: 'circle-area',
    conceptId: 'circles',
    description: 'Find the area of a circle from its radius.',
    difficulties: [3, 4],
    build: ({ rng, difficulty }) => {
      // r = 2 is left out: there πr² and 2πr are the same number, so the
      // circumference trap would be indistinguishable from a correct answer.
      const radius = rng.int(3, difficulty === 3 ? 12 : 25);
      const answer = roundTo(PI * radius * radius, 2);

      return numericExercise({
        prompt: `A circle has a radius of ${radius} cm. What is its area? Use π = ${PI}.`,
        unit: 'square cm',
        figure: { kind: 'shape', shape: 'circle', labels: { radius: `${radius} cm` } },
        value: answer,
        summary: `Area is πr², so A = ${PI} × ${radius}² = ${PI} × ${radius * radius} = ${answer} square cm.`,
        hint: 'Area covers the inside, so the radius is used twice: π × r × r.',
        steps: [
          { label: 'Square the radius.', latex: `${radius}^2 = ${radius * radius}` },
          { label: 'Multiply by π.', latex: `A = ${PI} \\times ${radius * radius} = ${answer}` },
        ],
        traps: [
          trap(roundTo(2 * PI * radius, 2), 'circumference-for-area'),
          trap(roundTo(PI * (radius * 2) * (radius * 2), 2), 'diameter-for-radius'),
        ],
      });
    },
  }),

  template({
    id: 'circle-radius-from-circumference',
    conceptId: 'circles',
    description: 'Work backwards from a circumference to the radius.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      const radius = rng.int(2, 20);
      const circumference = roundTo(2 * PI * radius, 2);

      return numericExercise({
        prompt: `A circle has a circumference of ${circumference} cm. What is its radius? Use π = ${PI}.`,
        unit: 'cm',
        value: radius,
        summary: `C = 2πr, so r = ${circumference} ÷ (2 × ${PI}) = ${radius} cm.`,
        hint: 'Undo the formula: divide by π to get the diameter, then halve it.',
        steps: [
          { label: 'Divide by π to get the diameter.', latex: `${circumference} \\div ${PI} = ${roundTo(circumference / PI, 2)}` },
          { label: 'Halve it to get the radius.', latex: `r = ${radius}` },
        ],
        traps: [trap(roundTo(circumference / PI, 2), 'diameter-for-radius')],
      });
    },
  }),

  template({
    id: 'surface-area-prism',
    conceptId: 'surface-area',
    description: 'Find the surface area of a rectangular prism.',
    difficulties: [3, 4],
    build: ({ rng, difficulty }) => {
      const limit = difficulty === 3 ? 8 : 14;
      const length = rng.int(2, limit);
      const width = rng.int(2, limit);
      const height = rng.int(2, limit);
      const faces = length * width + length * height + width * height;
      const answer = 2 * faces;
      const volume = length * width * height;

      return numericExercise({
        prompt: `A box measures ${length} cm by ${width} cm by ${height} cm. What is its surface area?`,
        unit: 'square cm',
        figure: {
          kind: 'prism',
          length,
          width,
          height,
          labels: { length: `${length} cm`, width: `${width} cm`, height: `${height} cm` },
        },
        value: answer,
        summary: `The three distinct faces have areas ${length * width}, ${length * height} and ${width * height}. Each has a twin, so the total is 2 × ${faces} = ${answer} square cm.`,
        hint: 'A box has six faces, in three matching pairs. Find one of each pair, add them, and double.',
        steps: [
          { label: 'Area of each distinct face.', latex: `${length * width},\\ ${length * height},\\ ${width * height}` },
          { label: 'Add them.', latex: `${faces}` },
          { label: 'Every face has a twin, so double.', latex: `2 \\times ${faces} = ${answer}` },
        ],
        traps: [
          ...(volume === answer ? [] : [trap(volume, 'surface-area-as-volume')]),
          // At 3 × 3 × 3 the sum of the three distinct faces is 27 and so is
          // the volume, which would make the two traps indistinguishable and
          // the student's diagnosis a coin toss.
          ...(faces === volume ? [] : [trap(faces, 'surface-area-faces-missed')]),
        ],
      });
    },
  }),

  template({
    id: 'surface-area-cube',
    conceptId: 'surface-area',
    description: 'Find the surface area of a cube.',
    difficulties: [2, 3],
    build: ({ rng, difficulty }) => {
      const side = rng.int(2, difficulty === 2 ? 9 : 15);
      const answer = 6 * side * side;

      return numericExercise({
        prompt: `A cube has edges of ${side} cm. What is its surface area?`,
        unit: 'square cm',
        figure: { kind: 'prism', length: side, width: side, height: side, labels: { length: `${side} cm` } },
        value: answer,
        summary: `Each face is ${side} × ${side} = ${side * side} square cm, and there are six of them: ${answer} square cm.`,
        hint: 'All six faces of a cube are identical squares.',
        steps: [
          { label: 'Area of one face.', latex: `${side}^2 = ${side * side}` },
          { label: 'Six faces.', latex: `6 \\times ${side * side} = ${answer}` },
        ],
        traps: [
          // s³ and 6s² coincide at s = 6, so that case drops the trap.
          ...(side === 6 ? [] : [trap(side * side * side, 'surface-area-as-volume')]),
          trap(side * side, 'surface-area-faces-missed'),
        ],
      });
    },
  }),

  /* ------------------------------------------------------- scale drawings */
  template({
    id: 'scale-drawing-to-real',
    conceptId: 'scale-drawings',
    description: 'Convert a drawing length to a real length using a scale.',
    difficulties: [2, 3],
    build: ({ rng, difficulty }) => {
      const scale = rng.pick(difficulty === 3 ? [25, 40, 50, 75, 100, 150, 200] : [10, 20, 25, 50]);
      const drawn = rng.int(2, 12);
      const real = drawn * scale;
      const scenario = rng.pick([
        { subject: 'A floor plan', noun: 'a wall' },
        { subject: 'A map', noun: 'a path' },
        { subject: 'A model railway drawing', noun: 'a bridge' },
      ]);

      return numericExercise({
        prompt: `${scenario.subject} is drawn to a scale of 1 : ${scale}. On the drawing ${scenario.noun} measures ${drawn} cm. How long is it really, in centimetres?`,
        unit: 'cm',
        value: real,
        summary: `Every centimetre on the drawing stands for ${scale} cm, so ${drawn} cm stands for ${drawn} × ${scale} = ${real} cm.`,
        hint: 'You are going from the drawing to the real thing, so the answer has to be bigger.',
        steps: [
          { label: 'Write the scale as a multiplier.', latex: `\\times ${scale}` },
          { label: 'Apply it to the drawing length.', latex: `${drawn} \\times ${scale} = ${real}` },
        ],
        traps: [
          // Dividing when the answer had to grow.
          trap(roundTo(drawn / scale, 4), 'scale-factor-inverted'),
          // Comparing by difference rather than by multiplication.
          ...(drawn + scale === real ? [] : [trap(drawn + scale, 'proportion-additive')]),
        ],
      });
    },
  }),

  template({
    id: 'scale-real-to-drawing',
    conceptId: 'scale-drawings',
    description: 'Work back from a real length to the length on the drawing.',
    difficulties: [3, 4],
    build: ({ rng, difficulty }) => {
      const scale = rng.pick(difficulty === 4 ? [25, 40, 50, 80, 125, 200] : [10, 20, 25, 50]);
      const drawn = rng.int(2, 15);
      const real = drawn * scale;

      return numericExercise({
        prompt: `A garden is ${real} cm long. It is to be drawn to a scale of 1 : ${scale}. How long should it be on the drawing, in centimetres?`,
        unit: 'cm',
        value: drawn,
        summary: `Going from real to drawing divides: ${real} ÷ ${scale} = ${drawn} cm.`,
        hint: 'The drawing is the small one, so this answer must come out smaller than the real length.',
        steps: [
          { label: 'The drawing is the real thing divided by the scale.', latex: `\\frac{${real}}{${scale}}` },
          { label: 'Divide.', latex: `${real} \\div ${scale} = ${drawn}` },
        ],
        traps: [
          // Multiplying when the answer had to shrink.
          trap(real * scale, 'scale-factor-inverted'),
          ...(real - scale === drawn ? [] : [trap(real - scale, 'proportion-additive')]),
        ],
      });
    },
  }),

  template({
    id: 'scale-area-enlargement',
    conceptId: 'scale-drawings',
    description: 'Find the area of an enlarged figure, where the factor squares.',
    difficulties: [4, 5],
    build: ({ rng, difficulty }) => {
      const k = rng.pick(difficulty === 5 ? [2, 3, 4, 5, 6, 10] : [2, 3, 4, 5]);
      const width = rng.int(2, 9);
      const height = rng.int(2, 9);
      const area = width * height;
      const answer = area * k * k;

      return numericExercise({
        prompt: `A rectangle measures ${width} cm by ${height} cm. Every length is multiplied by ${k}. What is the area of the enlarged rectangle, in square centimetres?`,
        unit: 'cm²',
        value: answer,
        summary: `The sides become ${width * k} cm and ${height * k} cm, so the area is ${answer} cm² — the original ${area} cm² multiplied by ${k}² = ${k * k}.`,
        hint: 'Both sides are multiplied, so the area is multiplied twice over.',
        steps: [
          { label: 'The original area.', latex: `${width} \\times ${height} = ${area}` },
          { label: 'Scale each side.', latex: `${width * k} \\times ${height * k}` },
          { label: 'Or scale the area by k².', latex: `${area} \\times ${k}^2 = ${answer}` },
        ],
        traps: [
          // Area scaled by k instead of k².
          trap(area * k, 'area-scaled-linearly'),
          // The scaled perimeter, offered because it is the other thing you can
          // compute from the same two numbers.
          ...([2 * (width + height) * k].filter((value) => value !== answer && value !== area * k).map((value) => trap(value, 'perimeter-for-area'))),
        ],
      });
    },
  }),

  template({
    id: 'scale-find-the-scale',
    conceptId: 'scale-drawings',
    description: 'Recover the scale factor from a matching pair of lengths.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const scale = rng.pick([10, 20, 25, 40, 50, 60, 80, 100, 150, 200]);
      const drawn = rng.int(2, 12);
      const real = drawn * scale;

      const { choices, correctId } = options(
        [
          { label: `1 : ${scale}`, correct: true },
          { label: `${scale} : 1`, misconceptionId: 'scale-factor-inverted' },
          { label: `1 : ${real - drawn}`, misconceptionId: 'proportion-additive' },
          { label: `1 : ${scale * scale}`, misconceptionId: 'area-scaled-linearly' },
        ],
        rng.shuffle,
      );

      return choiceExercise({
        prompt: `On a plan, a length of ${drawn} cm represents a real length of ${real} cm. What is the scale of the plan?`,
        choices,
        correctId,
        summary: `${real} ÷ ${drawn} = ${scale}, and the drawing is the smaller of the two, so the scale is 1 : ${scale}.`,
        hint: 'How many times bigger is the real length than the drawn one? The smaller number comes first.',
        steps: [
          { label: 'Divide the real length by the drawn length.', latex: `\\frac{${real}}{${drawn}} = ${scale}` },
          { label: 'Write it with the drawing first.', latex: `1 : ${scale}` },
        ],
      });
    },
  }),
];
